import { Input } from "@/components/ui/input";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/lib/utils";
import { ComponentProps, useId } from "react";
import { FilterColumn, NumberRange } from "..";

type FilterFieldBaseProps = Omit<
    ComponentProps<typeof Input>,
    "onChange" | "value"
>;

interface FilterFieldProps<T> extends FilterFieldBaseProps {
    column: FilterColumn<T>;
    value: NumberRange;
    onChange: (value: NumberRange) => void;
}

export default function NumberFilterField<T>({
    column,
    value,
    onChange,
    placeholder = "Enter value..",
    ...rest
}: FilterFieldProps<T>) {
    const minId = useId();
    const maxId = useId();

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMin = e.target.value === "" ? null : Number(e.target.value);
        onChange({ ...value, min: newMin });
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMax = e.target.value === "" ? null : Number(e.target.value);
        onChange({ ...value, max: newMax });
    };

    return (
        <div className={cn("flex flex-col gap-2")}>
            <Label className={cn("text-sm font-medium")}>
                {column.headingText}
            </Label>
            <div className={cn("flex items-center gap-2")}>
                <div className={cn("flex-1")}>
                    <Label
                        htmlFor={minId}
                        className={cn("text-xs text-muted-foreground")}
                    >
                        Min
                    </Label>
                    <Input
                        id={minId}
                        type="number"
                        placeholder={placeholder}
                        value={value.min ?? ""}
                        onChange={handleMinChange}
                        {...rest}
                    />
                </div>
                <span className={cn("pt-5 text-muted-foreground")}>—</span>
                <div className={cn("flex-1")}>
                    <Label
                        htmlFor={maxId}
                        className={cn("text-xs text-muted-foreground")}
                    >
                        Max
                    </Label>
                    <Input
                        id={maxId}
                        type="number"
                        placeholder={placeholder}
                        value={value.max ?? ""}
                        onChange={handleMaxChange}
                        {...rest}
                    />
                </div>
            </div>
        </div>
    );
}
